import { useEffect, useState, Fragment } from "react";
import clsx from "clsx";
import styled from "styled-components";
import { GridRowSelectionModel } from "@mui/x-data-grid-premium";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	MenuItem,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { cloneDeep } from "lodash-es";
import SimpleTextField from "@/Layouts/components/InputComponents/SimpleTextField";
import { IRow } from "@/Settings/types/SystemSettings/appendingConfiguration";

const resolveArithmeticValue = (value: string) => {
	if (value === "*") {
		return "×";
	}
	return value;
};

const StyledTextField = styled(TextField)`
	width: 200px;
	& > * {
		color: #666;
		& > * {
			padding-top: 3.5px;
			padding-bottom: 3.5px;
		}
	}
`;

type TArithmeticDialogProps = {
	openArithmeticDialog: boolean;
	setOpenArithmeticDialog: (value: boolean) => void;
	rows: IRow[];
	onSaveOperations: (data: IRow[]) => void;
	selectedRows: GridRowSelectionModel;
};

const ArithmeticDialog = (props: Readonly<TArithmeticDialogProps>) => {
	const { openArithmeticDialog, setOpenArithmeticDialog, rows, onSaveOperations, selectedRows } = props;

	const [filteredRows, setFilteredRows] = useState<IRow[]>([]);

	useEffect(() => {
		const clonnedRows = cloneDeep(rows).filter((item) => selectedRows.includes(item._id));
		setFilteredRows(clonnedRows);
		if (!clonnedRows.length) {
			setOpenArithmeticDialog(false);
		}
		return () => {
			setFilteredRows([]);
		};
	}, [selectedRows, openArithmeticDialog]);

	const handleInputChange = ({ _id, operation, value }: { _id: string; operation: string; value: string }) => {
		const isNegativeNumber = value === "-";
		const newFilteredRows = filteredRows.map((item) => {
			if (item._id === _id && (isNegativeNumber || !isNaN(Number(value)))) {
				item.arithmeticExpression = { operation, value: Number(value) };
			}
			return item;
		});
		setFilteredRows(newFilteredRows);
	};

	const handleSelectChange = ({
		_id,
		event,
		value,
	}: {
		_id: string;
		event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
		value: number;
	}) => {
		const newFilteredRows = filteredRows.map((item) => {
			if (item._id === _id) {
				item.arithmeticExpression = { operation: event.target.value, value: Number(value) };
			}
			return item;
		});
		setFilteredRows(newFilteredRows);
	};

	const hasErrors = filteredRows.some((item) => {
		const arithmeticValue = item.arithmeticExpression.value;
		return isNaN(arithmeticValue) || String(arithmeticValue) === "";
	});

	const handleClick = () => {
		onSaveOperations(filteredRows);
		setOpenArithmeticDialog(false);
	};

	return (
		<Dialog
			className={"dialog-br-15"}
			open={openArithmeticDialog}
			onClose={() => setOpenArithmeticDialog(false)}
			PaperProps={{
				style: {
					overflow: "hidden",
				},
			}}
		>
			<DialogTitle>
				<IconButton
					aria-label="close"
					onClick={() => setOpenArithmeticDialog(false)}
					sx={{
						position: "absolute",
						right: 5,
						top: 2,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<CloseRoundedIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent sx={{ padding: "0 20px" }}>
				{filteredRows.map((item) => {
					const accountDescription =
						typeof item?.accountDescription === "string" ? item?.accountDescription : item?.accountDescription?.dealerCodeFS;
					const glAccount = item?.glAccount?.[0]?.glAccount;
					const arithmeticOperation = item?.arithmeticExpression?.operation;
					const arithmeticValue = item?.arithmeticExpression?.value;
					return (
						<Fragment key={item._id}>
							<div style={{ width: "300px", paddingRight: "10px" }}>
								<Typography
									sx={{
										fontSize: "16px",
										fontWeight: "700",
										color: "var(--text-color)",
										margin: "5px 0 5px 0",
										textAlign: "left",
										overflowWrap: "break-word",
										inlineSize: "100%",
										wordBreak: "break-word",
										fontFamily: "PTSansProNarrow",
										lineHeight: "1.2",
										overflow: "hidden",
										whiteSpace: "nowrap",
										textOverflow: "ellipsis",
									}}
									title={accountDescription}
								>
									{accountDescription}
								</Typography>
							</div>
							<Stack key={item._id} direction="row" spacing={1} sx={{ width: "300px", marginBottom: "15px" }}>
								<SimpleTextField
									editable={false}
									disabled={false}
									readOnly={true}
									outlined
									text={glAccount}
									placeholder="GL Account"
									error={false}
								/>
								<StyledTextField
									select
									value={arithmeticOperation}
									onChange={(event) => handleSelectChange({ event, _id: item._id, value: arithmeticValue })}
									fullWidth
									size="small"
									className={"advanced-select regular-input-text"}
								>
									<MenuItem value="+">+</MenuItem>
									<MenuItem value="-">-</MenuItem>
									<MenuItem value="*">×</MenuItem>
									<MenuItem value="/">/</MenuItem>
								</StyledTextField>
								<SimpleTextField
									editable={true}
									disabled={false}
									outlined
									text={resolveArithmeticValue(String(arithmeticValue))}
									placeholder="Value"
									error={isNaN(arithmeticValue) || String(arithmeticValue) === ""}
									onChange={(value) => {
										handleInputChange({ _id: item._id, operation: arithmeticOperation, value });
									}}
								/>
							</Stack>
						</Fragment>
					);
				})}
			</DialogContent>
			<DialogActions>
				<Button
					variant="contained"
					className={clsx({
						"h-[30px] rounded-[6px]": true,
						"!bg-primary hover:!shadow-primary-shadow-50": !hasErrors,
						"!bg-disabled": hasErrors,
					})}
					onClick={handleClick}
					sx={{ margin: " 0 10px 10px" }}
					disabled={hasErrors}
				>
					<Typography className="select-plan-dialog-assign-btn-text">Save Operations</Typography>
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ArithmeticDialog;
